import React, { useState } from 'react';

// material-ui
import { Menu, MenuItem } from '@mui/material';
import IconButton from 'components/@extended/IconButton';

// assets
import { MoreOutlined } from '@ant-design/icons';

// project import
import { usePermissions } from 'hooks/usePermissions';
import { OrgPermissions } from 'utils/permissions';

// ==============================|| CREDENTIAL GRANT - ROW ACTIONS ||============================== //

/**
 * Uniform "3 dots" row menu — Edit access groups / Suspend / Reinstate / Revoke — gated to
 * `MANAGE_CREDENTIAL_GRANTS` and enabled per the grant's current status, mirroring the legal
 * transitions in `CredentialGrantStatusTransitionGuard` (active → suspended, suspended → active,
 * anything non-revoked → revoked). Wrapped in a `stopPropagation` span since the table row itself
 * opens the detail dialog on click.
 *
 * Hidden entirely (not just disabled) when `grant.is_agreement_paused` — a paused grant keeps its
 * row in Active / Pending Devices but is read-only. See sage-nest-api's
 * `credential-grant/CLAUDE.md` for the field.
 */
const CredentialGrantRowActions = ({ grant, onEditAccessGroups, onSuspend, onReinstate, onRevoke }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { hasPermission } = usePermissions();

    if (!hasPermission(OrgPermissions.MANAGE_CREDENTIAL_GRANTS)) return null;
    if (grant.is_agreement_paused) return null;

    const isRevoked = grant.status === 'revoked';
    const isActive = grant.status === 'active';
    const isSuspended = grant.status === 'suspended';

    const handleClose = () => setAnchorEl(null);

    const runAndClose = action => {
        handleClose();
        action(grant);
    };

    return (
        <span onClick={e => e.stopPropagation()}>
            <IconButton
                size="small"
                color="secondary"
                onClick={e => setAnchorEl(e.currentTarget)}
            >
                <MoreOutlined style={{ fontSize: '1.1rem' }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem disabled={isRevoked} onClick={() => runAndClose(onEditAccessGroups)}>
                    Edit access groups
                </MenuItem>
                <MenuItem disabled={!isActive} onClick={() => runAndClose(onSuspend)}>
                    Suspend
                </MenuItem>
                <MenuItem disabled={!isSuspended} onClick={() => runAndClose(onReinstate)}>
                    Reinstate
                </MenuItem>
                <MenuItem disabled={isRevoked} onClick={() => runAndClose(onRevoke)}>
                    Revoke
                </MenuItem>
            </Menu>
        </span>
    );
};

export default CredentialGrantRowActions;
